// The ONE affine camera every 2D CAD editor in this app shares.
//
// Three editors draw through a screen transform of the same shape — the room
// floor plan (`FloorplanView`), the ceiling profile (`CeilingView`), and the
// panel-taper plan view (`PanelTaperView`) — and before this module existed,
// two of them carried their own verbatim copies of the zoom/pan math
// (`zoomViewAt`/`panView` vs `zoomCeilingViewAt`/`panCeilingView`) while the
// third had no camera at all. Same story as the colour ramps FieldLegend.tsx's
// header documents: parallel copies drift. This module is the single
// implementation; each editor keeps only its own *fit* (what the camera is an
// offset from) and its own scale bounds (a room zooms 6..400 px/m, a profile
// 0.5x..8x of its fit) — the camera itself is not domain logic.
//
// A `CadView2D` composes UNDER a domain base mapping:
//   screen = cadViewToScreen(view, base(world))
// For the room the base mapping *is* the view (FloorplanView extends this
// shape structurally, so `zoomCadViewAt`'s spread-friendly return keeps its
// width/height/padding extras); for the ceiling the base is the fit geometry
// (`ceilingNodeToPx`); for the panel it's the centre-origin metre scale.

import type { PolyPt } from './polygon';

export interface CadView2D {
  scale: number;
  originX: number;
  originY: number;
}

/** The un-panned, un-zoomed camera — what "Fit" resets to for editors whose
 *  base mapping already fits the content. */
export const CAD_VIEW_IDENTITY: CadView2D = { scale: 1, originX: 0, originY: 0 };

/** Per-domain zoom limits. The numbers are domain decisions (metres-per-pixel
 *  for a room, multiples-of-fit for a profile); the clamping is not. */
export interface CadScaleBounds {
  min: number;
  max: number;
}

/** One wheel-tick-to-zoom-factor convention for every CAD surface, so no two
 *  editors ever zoom at visibly different speeds under the same wheel. */
export const CAD_WHEEL_ZOOM_SENSITIVITY = 0.0015;

export function wheelZoomFactor(deltaY: number): number {
  return Math.exp(-deltaY * CAD_WHEEL_ZOOM_SENSITIVITY);
}

export function cadViewToScreen(view: CadView2D, p: PolyPt): PolyPt {
  return { x: view.originX + p.x * view.scale, y: view.originY + p.y * view.scale };
}

export function screenToCadView(view: CadView2D, p: PolyPt): PolyPt {
  return { x: (p.x - view.originX) / view.scale, y: (p.y - view.originY) / view.scale };
}

/** Zoom about a fixed screen point — the point under the cursor stays under
 *  the cursor, which is the only zoom that feels like the drawing is being
 *  scaled rather than jumping. Returns the input unchanged (same reference)
 *  when the clamp swallows the whole factor, so callers can cheaply skip a
 *  re-render. The return type is the caller's own view type: extra fields
 *  beyond the camera (a FloorplanView's width/height/padding) ride along
 *  untouched. */
export function zoomCadViewAt<V extends CadView2D>(
  view: V,
  factor: number,
  screen: PolyPt,
  bounds: CadScaleBounds,
): V {
  const scale = Math.min(bounds.max, Math.max(bounds.min, view.scale * factor));
  if (scale === view.scale) return view;
  // Solve for the origin that keeps `screen` mapped to the same base point.
  const base = screenToCadView(view, screen);
  return {
    ...view,
    scale,
    originX: screen.x - base.x * scale,
    originY: screen.y - base.y * scale,
  };
}

/** Slide the frame by a screen-space delta. */
export function panCadView<V extends CadView2D>(view: V, dx: number, dy: number): V {
  if (dx === 0 && dy === 0) return view;
  return { ...view, originX: view.originX + dx, originY: view.originY + dy };
}

/** Whether a screen-space point has real presence inside the frame. The one
 *  shared definition of "on screen" for a DOM-rendered CAD label: a label
 *  whose anchor fails this is not rendered at all — hidden with its
 *  geometry, never clamped to the edge like a HUD element (the fix history
 *  is on `EdgeDimension.visible` in floorPolygon.ts). */
export function isPointInView(
  p: PolyPt,
  view: { width: number; height: number },
): boolean {
  return p.x >= 0 && p.x <= view.width && p.y >= 0 && p.y <= view.height;
}
